import React, { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import Navigation from '../components/Navigation';
import PDFViewer from '../components/PDFViewer';
import { usePermission } from '../contexts/PermissionContext';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { ContentsAPI, UploadGroupsAPI, UploadsAPI, S3API } from '../api/api';
import {
    Target,
    Award,
    Trophy,
    BookOpen,
    BarChart3,
    Briefcase,
    Users,
    Globe,
    Palmtree,
    FileText,
    FolderOpen,
    Building2,
    Sparkles,
    DollarSign,
    Gift,
    Medal,
    Gem,
    GraduationCap,
    Laptop,
    Sun,
    TrendingUp,
    Banknote,
    ExternalLink,
    MapPin,
    CircleDot,
    Cloud,
    Star,
    Database,
    ChevronRight,
    X,
    Image,
    Video
} from 'lucide-react';
import { LUCIDE_ICON_MAP } from '../components/LucideIconPicker';
import '../components/CommonCards.css';
import './OverviewPage.css';

function OverviewPage() {
    const { canAccessSection, isLoading } = usePermission();
    const { user } = useAuth();
    const { info, error: showError } = useAlert();

    // 관리자 역할 확인
    const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

    // API에서 받은 메뉴 데이터 (권한 필터링됨)
    const [menuData, setMenuData] = useState(null);
    const [isMenuLoading, setIsMenuLoading] = useState(true);

    // API 호출하여 권한 필터링된 메뉴 구조 가져오기
    const fetchMenuData = useCallback(async () => {
        try {
            setIsMenuLoading(true);
            const response = await ContentsAPI.getMenu();
            setMenuData(response.data);
        } catch (error) {
            console.error('메뉴 데이터 로드 실패:', error);
            setMenuData({ sections: [] });
        } finally {
            setIsMenuLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMenuData();
    }, [fetchMenuData]);

    // API 응답에서 접근 가능한 섹션 이름 목록 추출
    const accessibleSectionNames = useMemo(() => {
        if (!menuData?.sections) return new Set();
        return new Set(menuData.sections.map(section => section.name));
    }, [menuData]);

    // 섹션 접근 권한 체크 함수 (API 기반)
    const canAccessSectionByAPI = useCallback((sectionName) => {
        if (isAdmin) return true;
        return accessibleSectionNames.has(sectionName);
    }, [isAdmin, accessibleSectionNames]);

    // 각 섹션에 대한 ref 생성
    const visionRef = useRef(null);
    const recognitionRef = useRef(null);
    const rewardRef = useRef(null);
    const educationRef = useRef(null);

    // 모달 state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentImages, setCurrentImages] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [modalTitle, setModalTitle] = useState('');
    const [isBrochure, setIsBrochure] = useState(false);
    const [hasMonthlyTabs, setHasMonthlyTabs] = useState(false);
    const [monthlyData, setMonthlyData] = useState({});
    const [selectedMonth, setSelectedMonth] = useState('');
    const [imageOrientation, setImageOrientation] = useState('landscape');
    const [mediaItems, setMediaItems] = useState([]);
    const [currentMediaType, setCurrentMediaType] = useState('image');
    const [isFullscreen, setIsFullscreen] = useState(false); // 전체화면 상태

    // 그룹화 관련 상태
    const [uploadGroups, setUploadGroups] = useState([]);
    const [groupsLoading, setGroupsLoading] = useState(false);
    const [selectedPart, setSelectedPart] = useState(null);
    const [modalView, setModalView] = useState('files'); // 'groups' | 'files'
    const [selectedGroupTitle, setSelectedGroupTitle] = useState('');

    // 섹션으로 스크롤하는 함수
    const scrollToSection = (ref) => {
        if (ref.current) {
            const element = ref.current;
            const yOffset = -100; // 네비게이션 높이만큼 여유 (90px 네비게이션 - 10px 여유)
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

            window.scrollTo({
                top: y,
                behavior: 'smooth',
            });
        }
    };

    // 카드 클릭 핸들러
    const handleCardClick = async (card) => {
        console.log('=== Card Click Debug ===');
        console.log('Card clicked:', card.title);
        console.log('Card partId:', card.partId);

        // partId가 있는 경우 DB에서 그룹 로드 시도
        if (card.partId) {
            setSelectedPart(card);
            setModalTitle(card.title);

            // 먼저 API 호출 (모달 열기 전에)
            console.log('API 호출 시작...');
            const groups = await loadUploadGroups(card.partId);
            console.log('API 응답 groups:', groups);
            console.log('groups.length:', groups?.length);

            if (groups && groups.length > 0) {
                // 그룹이 있으면 그룹 선택 모달 표시
                console.log('modalView를 groups로 설정');
                setModalView('groups');
            } else if (card.images && card.images.length > 0) {
                // 그룹이 없지만 정적 이미지가 있으면 정적 이미지 표시
                console.log('그룹 없음, 정적 이미지로 폴백. 이미지 개수:', card.images.length);
                setCurrentImages(card.images);
                setMediaItems(card.images.map((src) => ({ type: 'image', src })));
                setCurrentImageIndex(0);
                setIsBrochure(card.isBrochure || false);
                setHasMonthlyTabs(false);
                setCurrentMediaType('image');
                setModalView('files');
            } else {
                // 그룹도 없고 정적 이미지도 없으면
                console.log('그룹도 이미지도 없음');
                info('등록된 자료가 없습니다.');
                return;
            }

            // 상태 설정 후 모달 열기
            console.log('모달 열기 (isModalOpen = true)');
            setIsModalOpen(true);
            document.body.style.overflow = 'hidden';
            return;
        }

        // 미디어 아이템이 있는 경우 (이미지 + 동영상)
        if (card.mediaItems && card.mediaItems.length > 0) {
            setMediaItems(card.mediaItems);
            setCurrentImageIndex(0);
            setModalTitle(card.title);
            const firstItem = card.mediaItems[0];
            setCurrentMediaType(firstItem.type);
            setCurrentImages(card.mediaItems.map((item) => item.src));
            setHasMonthlyTabs(false);
            setIsBrochure(false);
            setIsModalOpen(true);
            document.body.style.overflow = 'hidden';
        }
        // 월별 탭이 있는 경우
        else if (card.hasMonthlyTabs && card.monthlyData) {
            const months = Object.keys(card.monthlyData);
            const firstMonth = months[0];
            setMonthlyData(card.monthlyData);
            setSelectedMonth(firstMonth);
            setCurrentImages(card.monthlyData[firstMonth]);
            setCurrentImageIndex(0);
            setModalTitle(card.title);
            setHasMonthlyTabs(true);
            setIsBrochure(false);
            setMediaItems([]);
            setCurrentMediaType('image');
            setIsModalOpen(true);
            document.body.style.overflow = 'hidden';
        }
        // 이미지가 있는 경우 모달 열기
        else if (card.images && card.images.length > 0) {
            setCurrentImages(card.images);
            setMediaItems(card.images.map((src) => ({ type: 'image', src })));
            setCurrentImageIndex(0);
            setModalTitle(card.title);
            setIsBrochure(card.isBrochure || false);
            setHasMonthlyTabs(false);
            setCurrentMediaType('image');
            setIsModalOpen(true);
            document.body.style.overflow = 'hidden';
        }
        // URL이 있는 경우 새 탭에서 열기
        else if (card.url) {
            if (card.url.includes('example.com') || card.url === '') {
                info(`${card.title}\n\n실제 URL로 변경이 필요합니다.`);
            } else if (card.title === 'Top Of Meta' || card.title === 'Top Of Meta2') {
                window.open(
                    card.url,
                    '_blank',
                    'width=1200,height=900,scrollbars=yes,resizable=yes,menubar=yes,toolbar=yes,location=yes'
                );
            } else {
                window.open(card.url, '_blank', 'noopener,noreferrer');
            }
        }
        // 이미지도 URL도 없는 경우
        else {
            info('이 항목에는 연결된 콘텐츠가 없습니다.');
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setCurrentImages([]);
        setMediaItems([]);
        setCurrentImageIndex(0);
        setModalTitle('');
        setIsBrochure(false);
        setHasMonthlyTabs(false);
        setMonthlyData({});
        setSelectedMonth('');
        setImageOrientation('landscape');
        setCurrentMediaType('image');
        setIsFullscreen(false);
        // 그룹 관련 상태 초기화
        setUploadGroups([]);
        setSelectedPart(null);
        setModalView('files');
        setSelectedGroupTitle('');
        document.body.style.overflow = 'auto';
    };

    // 그룹 로드 함수
    const loadUploadGroups = async (partId) => {
        console.log('loadUploadGroups 호출, partId:', partId);
        setGroupsLoading(true);
        try {
            const response = await UploadGroupsAPI.getByPartId(partId);
            console.log('API 전체 응답:', response);
            console.log('response.data:', response.data);
            const groups = response.data?.groups || response.groups || [];
            console.log('파싱된 groups:', groups);
            setUploadGroups(groups);
            return groups;
        } catch (error) {
            console.error('그룹 로드 실패:', error);
            setUploadGroups([]);
            return [];
        } finally {
            setGroupsLoading(false);
        }
    };

    // 그룹 선택 핸들러
    const handleGroupSelect = async (group) => {
        try {
            setGroupsLoading(true);
            const response = await UploadsAPI.getUploadsByGroupId(group.id);
            const files = response.data?.uploads || response.uploads || [];

            if (files.length === 0) {
                info('이 그룹에 파일이 없습니다.');
                setGroupsLoading(false);
                return;
            }

            // URL을 병렬로 요청
            const urlResults = await Promise.all(
                files.map(async (file) => {
                    try {
                        const urlResponse = await S3API.getDownloadUrl(file.id, 'VIEW');
                        return urlResponse.data?.url || urlResponse.url;
                    } catch (err) {
                        console.error('URL 로드 실패:', err);
                        return null;
                    }
                })
            );

            // 결과를 동기적으로 빌드 (순서 보장)
            const items = [];
            const urls = [];
            files.forEach((file, index) => {
                const url = urlResults[index];
                if (url) {
                    const isPDF = file.mimeType === 'application/pdf' ||
                                  file.fileName?.toLowerCase().endsWith('.pdf');
                    const isVideo = file.fileType === 'VIDEO' || file.mimeType?.startsWith('video/');
                    items.push({
                        type: isPDF ? 'pdf' : isVideo ? 'video' : 'image',
                        src: url,
                        fileName: file.originalName || file.fileName
                    });
                    urls.push(url);
                }
            });

            if (urls.length > 0) {
                setCurrentImages(urls);
                setMediaItems(items);
                setCurrentImageIndex(0);
                setCurrentMediaType(items[0]?.type || 'image');
                setSelectedGroupTitle(group.title);
                setModalView('files');
            } else {
                showError('파일을 불러올 수 없습니다.');
            }
        } catch (err) {
            console.error('파일 로드 실패:', err);
            showError('파일을 불러오는 중 오류가 발생했습니다.');
        } finally {
            setGroupsLoading(false);
        }
    };

    // 그룹 목록으로 돌아가기
    const handleBackToGroups = () => {
        setModalView('groups');
        setCurrentImages([]);
        setMediaItems([]);
        setCurrentImageIndex(0);
        setSelectedGroupTitle('');
    };

    const nextImage = () => {
        const newIndex = (currentImageIndex + 1) % currentImages.length;
        setCurrentImageIndex(newIndex);
        if (mediaItems.length > 0) {
            setCurrentMediaType(mediaItems[newIndex].type);
        }
    };

    const prevImage = () => {
        const newIndex = (currentImageIndex - 1 + currentImages.length) % currentImages.length;
        setCurrentImageIndex(newIndex);
        if (mediaItems.length > 0) {
            setCurrentMediaType(mediaItems[newIndex].type);
        }
    };

    const goToMedia = (index) => {
        setCurrentImageIndex(index);
        if (mediaItems.length > 0) {
            setCurrentMediaType(mediaItems[index].type);
        }
    };

    // 월별 탭 변경 핸들러
    const handleMonthChange = (month) => {
        setSelectedMonth(month);
        setCurrentImages(monthlyData[month]);
        setCurrentImageIndex(0);
    };

    // ESC 키로 모달 닫기
    React.useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') closeModal();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, []);

    // 썸네일 자동 스크롤
    React.useEffect(() => {
        if (isModalOpen && currentImageIndex >= 0) {
            const thumbnailElement = document.querySelector(`.modal-thumbnail:nth-child(${currentImageIndex + 1})`);
            if (thumbnailElement) {
                thumbnailElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center',
                });
            }
        }
    }, [currentImageIndex, isModalOpen]);

    // 이미지 orientation 감지
    React.useEffect(() => {
        if (isModalOpen && currentImages.length > 0 && currentMediaType === 'image') {
            const img = new Image();
            img.src = currentImages[currentImageIndex];
            img.onload = () => {
                const isPortrait = img.naturalHeight > img.naturalWidth;
                if (isPortrait) {
                    setImageOrientation('portrait');
                } else {
                    setImageOrientation('landscape');
                }
            };
        }
    }, [currentImageIndex, currentImages, isModalOpen, currentMediaType]);

    // ========== 아이콘 매핑 함수 ==========
    const getIconComponent = (iconName, size = 24, className = '') => {
        // 1. Lucide 아이콘 이름으로 매칭 (새 형식)
        const LucideIcon = LUCIDE_ICON_MAP[iconName];
        if (LucideIcon) {
            return <LucideIcon size={size} className={className} />;
        }

        // 2. 기존 이모지 매핑 (하위 호환)
        const emojiMap = {
            // 메인 섹션 아이콘
            '🎯': Target,
            '⭐': Award,
            '🏆': Trophy,
            '📚': BookOpen,
            // 비전 섹션 아이콘
            '📊': BarChart3,
            '💼': Briefcase,
            '👥': Users,
            '🗺️': MapPin,
            '1️⃣': CircleDot,
            '2️⃣': CircleDot,
            '3️⃣': CircleDot,
            '🏢': Building2,
            '🆕': Sparkles,
            '☁️': Cloud,
            '🌐': Globe,
            '🌴': Palmtree,
            '📄': FileText,
            '📋': FolderOpen,
            '📖': BookOpen,
            // 보상 섹션 아이콘
            '💰': DollarSign,
            '🎁': Gift,
            '🏅': Medal,
            '💎': Gem,
            // 교육 섹션 아이콘
            '🎓': GraduationCap,
            '💻': Laptop,
            '☀️': Sun,
            '📈': TrendingUp,
            '💵': Banknote,
            '🔗': ExternalLink,
            '🗂️': Database,
            '🎉': Sparkles,
        };
        const EmojiIcon = emojiMap[iconName];
        if (EmojiIcon) {
            return <EmojiIcon size={size} className={className} />;
        }

        // 3. 폴백
        return <FileText size={size} className={className} />;
    };

    // ========== API 데이터 기반 섹션 데이터 변환 ==========
    const sectionDataMap = useMemo(() => {
        if (!menuData?.sections) return {};

        const map = {};
        menuData.sections.forEach(section => {
            const categories = (section.categories || []).map(category => ({
                id: category.id,
                title: category.name,
                description: category.description || '',
                icon: category.icon || '📋',
                items: (category.parts || []).map(part => ({
                    id: part.id,
                    partId: part.id,
                    title: part.name,
                    description: part.description || '',
                    icon: part.icon || '📄',
                    buttonText: part.buttonText || '바로가기',
                    url: part.partType === 'LINK' ? part.externalUrl : null,
                    partType: part.partType,
                    isBrochure: part.isBrochure || false,
                    thumbnailUrl: part.thumbnailUrl || null,
                    categoryName: category.name,
                })),
            }));
            categories.forEach(cat => { cat.count = cat.items.length; });
            map[section.name] = categories;
        });
        return map;
    }, [menuData]);

    // 섹션 아이콘 매핑 (섹션 헤더용)
    const sectionIconMap = useMemo(() => {
        if (!menuData?.sections) return {};
        const map = {};
        menuData.sections.forEach(section => {
            map[section.name] = section.icon || '📄';
        });
        return map;
    }, [menuData]);

    // ========== 섹션 설정 (렌더링 순서, CSS 클래스, ref 매핑) ==========
    const SECTION_CONFIG = useMemo(() => [
        { name: '비전', id: 'vision', cssClass: 'vision-section', category: 'Vision', defaultIcon: '🎯', ref: visionRef },
        { name: '인정', id: 'recognition', cssClass: 'recognition-section', category: 'Recognition', defaultIcon: '⭐', ref: recognitionRef },
        { name: '보상', id: 'reward', cssClass: 'reward-section', category: 'Reward', defaultIcon: '🏆', ref: rewardRef },
        { name: '교육', id: 'education', cssClass: 'education-section', category: 'Education', defaultIcon: '📚', ref: educationRef },
    ], []);

    // 사이드바 네비게이션 아이템 (API 데이터 기반 동적 생성)
    const sidebarNavItems = useMemo(() => {
        return SECTION_CONFIG
            .filter(config => isAdmin || canAccessSectionByAPI(config.name))
            .map(config => {
                const categories = sectionDataMap[config.name] || [];
                const categoryCount = categories.filter(c => c.items.length > 0).length;
                const partCount = categories.reduce((sum, c) => sum + c.items.length, 0);
                return {
                    ...config,
                    icon: sectionIconMap[config.name] || config.defaultIcon,
                    title: config.name,
                    sectionName: config.name,
                    sections: categoryCount,
                    items: partCount,
                };
            });
    }, [SECTION_CONFIG, isAdmin, canAccessSectionByAPI, sectionDataMap, sectionIconMap]);

    return (
        <div className="overview-page">
            <Navigation />

            {/* 사이드바 네비게이션 */}
            <div className="sidebar-navigation">
                {sidebarNavItems.map((item) => (
                    <div key={item.id} className="sidebar-nav-item" onClick={() => scrollToSection(item.ref)}>
                        <div className="sidebar-nav-header">
                            <span className="sidebar-nav-icon">{getIconComponent(item.icon, 24)}</span>
                            <h3 className="sidebar-nav-title">{item.title}</h3>
                        </div>
                        <div className="sidebar-nav-info">
                            <div className="sidebar-nav-row">
                                <span className="sidebar-nav-label">카테고리</span>
                                <span className="sidebar-nav-value">{item.category}</span>
                            </div>
                            <div className="sidebar-nav-row">
                                <span className="sidebar-nav-label">섹션</span>
                                <span className="sidebar-nav-value highlight">{item.sections}개</span>
                            </div>
                            <div className="sidebar-nav-row">
                                <span className="sidebar-nav-label">항목</span>
                                <span className="sidebar-nav-value highlight">{item.items}개</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="overview-container">
                {/* 로딩 상태 */}
                {isMenuLoading && (
                    <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
                    </div>
                )}

                {/* 섹션 동적 렌더링 */}
                {!isMenuLoading && SECTION_CONFIG.map(config => {
                    if (!isAdmin && !canAccessSectionByAPI(config.name)) return null;
                    const categories = sectionDataMap[config.name] || [];
                    if (categories.length === 0) return null;

                    return (
                    <div key={config.id} ref={config.ref} className={`main-section ${config.cssClass}`}>
                        <div className="main-section-header">
                            <h2 className="main-section-title">
                                <span className="main-section-icon">
                                    {getIconComponent(sectionIconMap[config.name] || config.defaultIcon, 28)}
                                </span>
                                {config.name}
                            </h2>
                        </div>

                        {categories.map((section) => {
                            if (section.items.length === 0) return null;
                            return (
                            <div key={section.id} className="content-section">
                                <div className="section-header">
                                    <h3 className="section-title">{section.title}</h3>
                                    <span className="section-count">{section.items.length}</span>
                                </div>

                                <div className="cards-grid">
                                    {section.items.map((card) => (
                                        <div key={card.id} className="content-card" onClick={() => handleCardClick(card)}>
                                            <div className="card-icon-wrapper">
                                                <span className="card-icon">{getIconComponent(card.icon, 28)}</span>
                                            </div>
                                            <div className="card-info">
                                                <h4 className="card-title">{card.title}</h4>
                                                {card.description && (
                                                    <p className="card-description">{card.description}</p>
                                                )}
                                            </div>
                                            <button
                                                className="card-link-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCardClick(card);
                                                }}
                                            >
                                                {card.buttonText || '바로가기'} →
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            );
                        })}
                    </div>
                    );
                })}
            </div>

            {/* 그룹 선택 모달 */}
            {isModalOpen && modalView === 'groups' && (
                <div className="modal-overlay group-modal-overlay" onClick={closeModal}>
                    <div className="group-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="group-modal-header">
                            <div className="group-modal-title-section">
                                <h2 className="group-modal-title">{modalTitle}</h2>
                                <span className="group-modal-count">{uploadGroups.length}개 자료</span>
                            </div>
                            <button className="modal-close-btn" onClick={closeModal}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="group-modal-body">
                            {groupsLoading ? (
                                <div className="groups-loading">
                                    <div className="loading-spinner small"></div>
                                    <span>자료 목록을 불러오는 중...</span>
                                </div>
                            ) : uploadGroups.length === 0 ? (
                                <div className="groups-empty">
                                    <FolderOpen size={48} strokeWidth={1} />
                                    <span>등록된 자료가 없습니다.</span>
                                </div>
                            ) : (
                                <div className="groups-list">
                                    {uploadGroups.map((group) => (
                                        <div
                                            key={group.id}
                                            className="group-item"
                                            onClick={() => handleGroupSelect(group)}
                                        >
                                            <div className="group-item-icon">
                                                <FolderOpen size={24} />
                                            </div>
                                            <div className="group-item-info">
                                                <span className="group-item-title">{group.title}</span>
                                                <span className="group-item-count">{group.fileCount || 0}개 파일</span>
                                            </div>
                                            <ChevronRight size={20} className="group-item-arrow" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* 파일/PDF 뷰어 모달 */}
            {isModalOpen && modalView === 'files' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <button
                        className="modal-nav-btn modal-prev-btn"
                        onClick={(e) => { e.stopPropagation(); prevImage(); }}
                        disabled={currentImages.length <= 1}
                    >
                        ‹
                    </button>

                    <div
                        className={`modal-container ${isFullscreen ? 'fullscreen' : ''} ${currentMediaType === 'pdf' && mediaItems.length <= 1 ? 'pdf-layout' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-header">
                            <div className="modal-title-section">
                                {/* 그룹에서 온 경우 뒤로가기 버튼 */}
                                {selectedPart?.partId && uploadGroups.length > 0 && (
                                    <button className="back-to-groups-btn" onClick={handleBackToGroups}>
                                        ◀ 목록
                                    </button>
                                )}
                                <h2 className="modal-title">
                                    {selectedGroupTitle || modalTitle}
                                </h2>
                                {hasMonthlyTabs && (
                                    <select
                                        className="month-dropdown"
                                        value={selectedMonth}
                                        onChange={(e) => handleMonthChange(e.target.value)}
                                    >
                                        {Object.keys(monthlyData).map((month) => (
                                            <option key={month} value={month}>
                                                {month}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                            <div className="modal-controls">
                                <span className="modal-counter">
                                    {currentImageIndex + 1} / {currentImages.length}
                                </span>
                                {/* PDF가 아닐 때만 전체화면 버튼 표시 (PDF는 뷰어 내장 기능 사용) */}
                                {currentMediaType !== 'pdf' && (
                                    <button className="modal-fullscreen-btn" onClick={() => setIsFullscreen(!isFullscreen)}>
                                        {isFullscreen ? '축소' : '전체화면'}
                                    </button>
                                )}
                                <button
                                    className="modal-close-btn"
                                    onClick={isFullscreen ? () => setIsFullscreen(false) : closeModal}
                                >
                                    ✕
                                </button>
                            </div>
                        </div>

                        <div className="modal-body">
                            {/* 전체화면 축소 버튼 */}
                            {isFullscreen && (
                                <button
                                    className="fullscreen-close-btn"
                                    onClick={() => setIsFullscreen(false)}
                                    title="축소"
                                >
                                    ✕
                                </button>
                            )}

                            <div className="modal-image-container">
                                {currentMediaType === 'video' ? (
                                    <video
                                        key={currentImages[currentImageIndex]}
                                        src={currentImages[currentImageIndex]}
                                        controls
                                        autoPlay
                                        className="modal-video"
                                        style={{ maxWidth: '95%', maxHeight: '90%', width: 'auto', height: 'auto' }}
                                    >
                                        Your browser does not support the video tag.
                                    </video>
                                ) : currentMediaType === 'pdf' ? (
                                    <PDFViewer
                                        fileUrl={currentImages[currentImageIndex]}
                                        fileName={mediaItems[currentImageIndex]?.fileName}
                                    />
                                ) : (
                                    <img
                                        src={currentImages[currentImageIndex]}
                                        alt={`${modalTitle} - 슬라이드 ${currentImageIndex + 1}`}
                                        className={`modal-image ${isBrochure ? 'brochure-image' : ''} ${
                                            imageOrientation === 'portrait' ? 'portrait' : 'landscape'
                                        }`}
                                        style={
                                            imageOrientation === 'portrait'
                                                ? {
                                                      maxWidth: '60%',
                                                      maxHeight: '100%',
                                                      height: '100%',
                                                      width: 'auto',
                                                  }
                                                : {
                                                      maxWidth: '95%',
                                                      maxHeight: '90%',
                                                      width: 'auto',
                                                      height: 'auto',
                                                  }
                                        }
                                    />
                                )}
                            </div>

                        </div>

                        <div className="modal-footer">
                            <div className="modal-thumbnails">
                                {currentImages.map((src, index) => {
                                    const mediaType = mediaItems[index]?.type;
                                    return (
                                        <div
                                            key={index}
                                            className={`modal-thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                                            onClick={() => goToMedia(index)}
                                        >
                                            {mediaType === 'pdf' ? (
                                                <FileText size={18} />
                                            ) : mediaType === 'video' ? (
                                                <Video size={18} />
                                            ) : (
                                                <Image size={18} />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <button
                        className="modal-nav-btn modal-next-btn"
                        onClick={(e) => { e.stopPropagation(); nextImage(); }}
                        disabled={currentImages.length <= 1}
                    >
                        ›
                    </button>
                </div>
            )}
        </div>
    );
}

export default OverviewPage;
